import qs from 'qs';

import { OfferingPatchRequest, OfferingPostRequest } from '@arrived/forms';
import {
  ApiError,
  ApiResponse,
  Offering,
  OfferingId,
  OfferingIdOrOfferingShortName,
  OfferingPropertyRequest,
  OfferingPropertyValuation,
  OfferingQueryParams,
  OfferingSearchQuery,
  OfferingTradesFilters,
  OfferingTransaction,
  OfferingTransactionReview,
  TotalAvailableSharesResponse,
} from '@arrived/models';

import { api, publicApi } from './api';
import { getQueryStringFromFilters, unwrapApiResponse } from './utils';

/**
 * @deprecated - Recommend using offerings via search which is using "Open Search"
 */
export const getOfferings = async (query?: OfferingQueryParams, isPublic?: boolean) => {
  const queryParameters = query ? getQueryStringFromFilters<OfferingQueryParams>(query) : '';

  const response = await (isPublic ? publicApi : api).get<ApiResponse<Offering[]>>(`/offerings${queryParameters}`);

  return unwrapApiResponse(response.data);
};

export interface OfferingSearchResponse {
  data: Offering[];
  error: ApiError | undefined;
  pagination: {
    page: number;
    size: number;
    totalResults: number;
  };
}

/**
 * This regex matches any sort query where a sort on propertyRank is present and a sort on id or name is not present.
 * It's important (as of the writing of this comment) that the sort includes at least one guaranteed ordering (like id
 * or name) because many Offerings have the same propertyRank and when indexed by OpenSearch won't always return in the
 * same order. This can lead to issues where different pages have the same Offering and if we render both copies of
 * the Offering we can get duplicate key issues.
 */
const sortRegex = /^(?!.*(id|name):)(?=.*propertyRank:).*$/;

export const getOfferingsSearch = async (query?: OfferingSearchQuery) => {
  if (query?.sort?.match(sortRegex)) {
    throw new Error(
      'Query sorts by propertyRank but not id or name, sorts by propertyRank must include a sort on an attribute with guaranteed ordering'
    );
  }

  const params = qs.stringify(query, { allowDots: true, arrayFormat: 'comma' });

  const { data: abacusResponse } = await api.get<OfferingSearchResponse>(
    `/offerings/search${params.length > 0 ? `?${params}` : ''}`
  );

  if (abacusResponse.error != null) {
    throw abacusResponse.error;
  }

  return abacusResponse;
};

export const getOffering = async (offeringIdOrOfferingShortName: OfferingIdOrOfferingShortName) => {
  const response = await api.get<ApiResponse<Offering>>(`/offerings/${offeringIdOrOfferingShortName}`);

  return unwrapApiResponse(response.data);
};

export const getOfferingReviews = async (offeringId: OfferingId) => {
  const response = await api.get<ApiResponse<OfferingTransactionReview>>(`/offerings/${offeringId}/reviews`);

  return unwrapApiResponse(response.data);
};

export const getOfferingValuations = async (offeringId: OfferingId) => {
  const response = await api.get<ApiResponse<OfferingPropertyValuation>>(`/offerings/${offeringId}/valuations`);

  return unwrapApiResponse(response.data);
};

export const postOffering = async (request: OfferingPostRequest) => {
  const response = await api.post<ApiResponse<Offering>>('/offerings', request);

  return unwrapApiResponse(response.data);
};

export const patchOffering = async (offeringId: OfferingId, request: OfferingPatchRequest) => {
  const response = await api.patch<ApiResponse<Offering>>(`/offerings/${offeringId}`, request);

  return unwrapApiResponse(response.data);
};

export const addPropertyToOffering = async (request: OfferingPropertyRequest) => {
  const response = await api.post<ApiResponse<Offering>>(`/offerings/${request.id}/properties`, request);

  return unwrapApiResponse(response.data);
};

export const patchPropertiesForOffering = async (request: OfferingPropertyRequest) => {
  const response = await api.patch<ApiResponse<Offering>>(`/offerings/${request.id}/properties`, request);

  return unwrapApiResponse(response.data);
};

export const removePropertyFromOffering = async (offeringId: OfferingId) => {
  const response = await api.delete<ApiResponse<Offering>>(`/offerings/${offeringId}/properties`);

  return unwrapApiResponse(response.data);
};

export const getOfferingTrades = async (offeringId: OfferingId, query?: OfferingTradesFilters) => {
  const queryParameters = query ? getQueryStringFromFilters<OfferingTradesFilters>(query) : '';

  const response = await api.get<ApiResponse<OfferingTransaction>>(`/offerings/${offeringId}/trades${queryParameters}`);

  return unwrapApiResponse(response.data);
};

export const getSharesAvailableForOffering = async (offeringId: OfferingId) => {
  const response = await api.get<ApiResponse<TotalAvailableSharesResponse>>(
    `/offerings/${offeringId}/shares/available`
  );

  return unwrapApiResponse(response.data);
};
